import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/repasse')({ component: AdminRepasse });

type TabKey = 'NF_APPROVED' | 'PAID' | 'ALL';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'NF_APPROVED', label: 'A pagar' },
  { key: 'PAID', label: 'Pagos' },
  { key: 'ALL', label: 'Todos' },
];

const STATUS_LABEL: Record<string, string> = {
  NF_APPROVED: 'NF aprovada', PAID: 'Pago',
  CLOSED: 'Fechado', NF_PENDING: 'Aguardando NF', NF_REVIEW: 'NF em análise',
  NF_REJECTED: 'NF rejeitada', DISPUTED: 'Em disputa',
};
const STATUS_BADGE: Record<string, string> = {
  NF_APPROVED: 'bg-teal-50 text-teal-700 border-teal-200',
  PAID: 'bg-green-50 text-green-700 border-green-200',
  CLOSED: 'bg-slate-100 text-slate-600 border-slate-200',
  NF_PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  NF_REVIEW: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  NF_REJECTED: 'bg-red-50 text-red-600 border-red-200',
  DISPUTED: 'bg-orange-50 text-orange-700 border-orange-200',
};

const money = (v: any) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type SessionLine = {
  id: string;
  scheduledAt: string;
  clientName: string | null;
  priceCharged: number;
  platformFeePercent: number;
  planSnapshot: string | null;
};

type ValidationStatus = 'NO_NF' | 'ERROR' | 'WARNING' | 'SUCCESS' | 'LEGACY';

type Payout = {
  id: string;
  therapistId: string;
  therapistName: string | null;
  therapistEmail: string;
  pixKey: string | null;
  pixKeyType: string | null;
  periodStart: string;
  periodEnd: string;
  totalSessions: number;
  grossAmount: number;
  platformFee: number;
  netAmount: number;
  status: string;
  payoutExecutedAt: string | null;
  pixTransactionId: string | null;
  invoiceNF: {
    nfsePdfUrl: string | null;
    nfseNumber: string | null;
    validationMessages: string[];
  } | null;
  validationStatus: ValidationStatus;
  sessions: SessionLine[];
};

function AdminRepasse() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('NF_APPROVED');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [txId, setTxId] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-payouts', tab],
    queryFn: () => api.get(`admin/payouts?status=${tab}`).json<{ items: Payout[]; total: number; pages: number }>(),
  });

  // Total a pagar (sempre da fila NF_APPROVED, mesmo quando outra aba está selecionada)
  const { data: queueData } = useQuery({
    queryKey: ['admin-payouts-queue'],
    queryFn: () => api.get('admin/payouts?status=NF_APPROVED').json<{ items: Payout[]; total: number }>(),
  });
  const totalReady = queueData?.items.reduce((s, c) => s + c.netAmount, 0) ?? 0;
  const queueCount = queueData?.total ?? 0;

  const { data: balance, isFetching: balanceLoading, refetch: refetchBalance } = useQuery({
    queryKey: ['admin-inter-balance'],
    queryFn: () =>
      api.get('admin/payouts/balance').json<{
        accountId?: string;
        accountName?: string;
        balance?: { total: number; available: number; blocked: number };
        currency?: string;
        error?: string;
      }>(),
    staleTime: 30_000,
  });

  const items = data?.items ?? [];

  const resetPayout = useMutation({
    mutationFn: (cycleId: string) => api.post(`admin/cycles/${cycleId}/reset-payout`).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-payouts'] });
      qc.invalidateQueries({ queryKey: ['admin-payouts-queue'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
    },
    onError: async (err: any) => {
      let msg = 'Falha ao resetar repasse.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      alert(msg);
    },
  });

  const seedTestCycle = useMutation({
    mutationFn: () => api.post('admin/test/seed-payout-cycle', { json: {} }).json<any>(),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['admin-payouts'] });
      qc.invalidateQueries({ queryKey: ['admin-payouts-queue'] });
      alert(`Ciclo de teste criado!\nCycle ${r.cycleId?.slice(-8)} · líquido R$ ${Number(r.netAmount).toFixed(2)} pronto pra repasse.`);
    },
    onError: async (err: any) => {
      let msg = 'Falha ao criar ciclo de teste.';
      try { const body = await err?.response?.json?.(); if (body?.message) msg = body.message; } catch {}
      alert(msg);
    },
  });

  const executePayout = useMutation({
    mutationFn: (vars: { cycleId: string; pixTransactionId?: string; needsApprove?: boolean }) =>
      api.patch(
        `admin/cycles/${vars.cycleId}/${vars.needsApprove ? 'approve-and-pay-out' : 'pay-out'}`,
        { json: { pixTransactionId: vars.pixTransactionId } },
      ).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-payouts'] });
      qc.invalidateQueries({ queryKey: ['admin-payouts-queue'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      setConfirmingId(null);
      setTxId('');
      setErrorMsg(null);
    },
    onError: async (err: any) => {
      let msg = 'Não foi possível registrar o repasse.';
      try {
        const body = await err?.response?.json?.();
        if (typeof body?.message === 'string') msg = body.message;
        else if (Array.isArray(body?.message)) msg = body.message.join(' · ');
      } catch { /* keep default */ }
      setErrorMsg(msg);
    },
  });

  const toggleConfirm = (id: string) => {
    if (confirmingId === id) { setConfirmingId(null); setTxId(''); setErrorMsg(null); }
    else { setConfirmingId(id); setTxId(''); setErrorMsg(null); }
  };

  return (
    <>
      <AdminPageHeader
        title="Repasses"
        subtitle={`${queueCount} aguardando · ${money(totalReady)} a repassar`}
      />

      <InterBalanceCard
        loading={balanceLoading}
        error={balance?.error}
        balance={balance?.balance}
        totalReady={totalReady}
        onRefresh={() => refetchBalance()}
      />

      <div className="mb-4 -mt-2 flex justify-end">
        <button
          onClick={() => {
            if (confirm('Criar/atualizar ciclo de teste (R$ 0,30 líquido) a partir da sessão online existente? Idempotente.')) {
              seedTestCycle.mutate();
            }
          }}
          disabled={seedTestCycle.isPending}
          className="text-[11px] text-slate-500 hover:text-slate-700 underline"
        >
          {seedTestCycle.isPending ? 'Criando...' : '⚙ Seed ciclo de teste (valor pequeno)'}
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setConfirmingId(null); setTxId(''); setErrorMsg(null); }}
            className={cn(
              'px-4 py-1.5 rounded-full text-sm font-medium transition border',
              tab === t.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-slate-400">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">
            {tab === 'NF_APPROVED'
              ? 'Nenhum ciclo com NF aprovada. Os ciclos aparecem aqui após fechamento, envio e aprovação da NF.'
              : 'Nenhum ciclo nesta aba.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Profissional</th>
                  <th className="text-left px-4 py-3 font-semibold">Período · Sessões</th>
                  <th className="text-right px-4 py-3 font-semibold">Bruto</th>
                  <th className="text-right px-4 py-3 font-semibold">Líquido</th>
                  <th className="text-left px-4 py-3 font-semibold">PIX</th>
                  <th className="text-left px-4 py-3 font-semibold">NF · Validação</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => {
                  const isOpen = confirmingId === c.id;
                  const isExpanded = expandedId === c.id;
                  // Pode pagar se NF está aprovada OU se ainda em review com validação SUCCESS
                  const canPayout =
                    c.status === 'NF_APPROVED' ||
                    (c.status === 'NF_REVIEW' && c.validationStatus === 'SUCCESS');
                  // Precisa aprovar atomicamente antes de pagar?
                  const needsApprove = c.status === 'NF_REVIEW';
                  return (
                    <>
                      <tr key={c.id} className={cn('hover:bg-slate-50/60', c.status === 'PAID' && 'opacity-70')}>
                        <td className="px-4 py-3 min-w-0">
                          <div className="font-semibold text-slate-800 text-xs leading-tight truncate">
                            {c.therapistName ?? '—'}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">{c.therapistEmail}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span>
                              {new Date(c.periodStart).toLocaleDateString('pt-BR')} →{' '}
                              {new Date(c.periodEnd).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : c.id)}
                            className="text-[11px] text-teal-600 hover:underline mt-0.5 whitespace-nowrap"
                          >
                            {isExpanded ? '▾' : '▸'} {c.totalSessions === 1 ? '1 sessão' : `${c.totalSessions} sessões`}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600 text-xs">
                          {money(c.grossAmount)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-bold text-teal-700">
                          {money(c.netAmount)}
                          <div className="text-[10px] font-normal text-slate-400 mt-0.5">
                            taxa - {money(c.platformFee)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {c.pixKey ? (
                            <div className="text-xs">
                              <div className="text-slate-400 text-[10px] uppercase">{c.pixKeyType ?? '—'}</div>
                              <div className="font-mono text-slate-700 truncate max-w-[160px]">{c.pixKey}</div>
                            </div>
                          ) : (
                            <span className="text-[11px] text-amber-600 font-medium">⚠ Não cadastrado</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <ValidationCell payout={c} />
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', STATUS_BADGE[c.status] ?? 'bg-slate-100 text-slate-600')}>
                            {STATUS_LABEL[c.status] ?? c.status}
                          </span>
                          {c.payoutExecutedAt && (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              {new Date(c.payoutExecutedAt).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {canPayout ? (
                            <Button
                              size="sm"
                              onClick={() => toggleConfirm(c.id)}
                              className={cn('h-7 text-xs px-2.5', isOpen ? 'bg-slate-200 text-slate-600' : 'bg-teal-500 hover:bg-teal-600 text-white')}
                            >
                              {isOpen ? 'Cancelar' : 'Fazer repasse'}
                            </Button>
                          ) : c.status === 'NF_REVIEW' ? (
                            <Link
                              to="/admin/notas-fiscais"
                              hash={`cycle-${c.id}`}
                              className="inline-flex items-center text-xs font-semibold text-coral-600 hover:underline whitespace-nowrap"
                              title="A validação automática encontrou problemas — revise antes de aprovar."
                            >
                              Revisar NF →
                            </Link>
                          ) : c.status === 'NF_PENDING' ? (
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">Aguardando NF</span>
                          ) : c.pixTransactionId ? (
                            <div className="flex flex-col items-end gap-1">
                              <code className="text-[10px] text-slate-500 font-mono" title={c.pixTransactionId}>
                                {c.pixTransactionId.length > 14 ? `${c.pixTransactionId.slice(0, 12)}…` : c.pixTransactionId}
                              </code>
                              <button
                                onClick={() => {
                                  if (confirm('Resetar repasse?\n\nO ciclo voltará pra "A pagar". Use só pra testar de novo.')) {
                                    resetPayout.mutate(c.id);
                                  }
                                }}
                                disabled={resetPayout.isPending}
                                className="text-[10px] text-slate-400 hover:text-coral-600 underline"
                                title="Volta o ciclo pra NF_APPROVED"
                              >
                                ↶ resetar
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${c.id}-sessions`}>
                          <td colSpan={8} className="bg-slate-50/40 px-4 py-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                              Sessões cobertas — taxa por sessão (snapshot do plano vigente no fechamento)
                            </p>
                            <table className="w-full text-xs">
                              <thead className="text-slate-400 uppercase tracking-wide text-[10px]">
                                <tr>
                                  <th className="text-left py-1.5 font-semibold">Data</th>
                                  <th className="text-left py-1.5 font-semibold">Cliente</th>
                                  <th className="text-center py-1.5 font-semibold">Plano · Taxa</th>
                                  <th className="text-right py-1.5 font-semibold">Cobrado</th>
                                  <th className="text-right py-1.5 font-semibold">Taxa</th>
                                  <th className="text-right py-1.5 font-semibold">Líquido</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {c.sessions.map((s) => {
                                  const fee = s.priceCharged * (s.platformFeePercent / 100);
                                  const net = s.priceCharged - fee;
                                  const planClass =
                                    s.planSnapshot === 'PREMIUM' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                    s.planSnapshot === 'PRO' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                                    'bg-slate-50 text-slate-600 border-slate-200';
                                  return (
                                    <tr key={s.id}>
                                      <td className="py-1.5 text-slate-600 whitespace-nowrap">
                                        {new Date(s.scheduledAt).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                                      </td>
                                      <td className="py-1.5 text-slate-700">{s.clientName ?? '—'}</td>
                                      <td className="py-1.5 text-center">
                                        <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', planClass)}>
                                          {s.planSnapshot ?? '—'} · {s.platformFeePercent}%
                                        </span>
                                      </td>
                                      <td className="py-1.5 text-right tabular-nums text-slate-700">{money(s.priceCharged)}</td>
                                      <td className="py-1.5 text-right tabular-nums text-coral-600">- {money(fee)}</td>
                                      <td className="py-1.5 text-right tabular-nums font-bold text-teal-700">{money(net)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      {isOpen && (
                        <tr key={`${c.id}-form`}>
                          <td colSpan={8} className="bg-slate-50/60 px-4 py-4">
                            <div className="space-y-3 max-w-2xl">
                              {errorMsg && (
                                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                                  <span className="font-semibold">{errorMsg}</span>
                                  <div className="mt-1 text-red-500">
                                    Informe o ID da transação manualmente abaixo para registrar o repasse.
                                  </div>
                                </div>
                              )}
                              {c.pixKey ? (
                                <div className="text-xs text-slate-600 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
                                  <strong>PIX automático via Banco Inter.</strong>{' '}
                                  Vamos transferir <strong className="text-teal-700">{money(c.netAmount)}</strong> para a chave{' '}
                                  <code className="font-mono text-slate-700">{c.pixKey}</code>{' '}
                                  ({c.pixKeyType?.toUpperCase() ?? '—'}).
                                </div>
                              ) : (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                  <strong>⚠ Profissional sem chave PIX cadastrada.</strong>{' '}
                                  Faça a transferência manualmente e cole o ID abaixo.
                                </div>
                              )}
                              <div className="flex items-end gap-3 flex-wrap">
                                <label className="flex-1 min-w-[260px] block">
                                  <span className="text-xs text-slate-600 font-medium">
                                    ID da transação PIX{' '}
                                    <span className="text-slate-400 font-normal">
                                      {c.pixKey ? '(opcional — preenchido pelo PIX automático se vazio)' : '(obrigatório)'}
                                    </span>
                                  </span>
                                  <input
                                    value={txId}
                                    onChange={(e) => setTxId(e.target.value)}
                                    placeholder="Ex: E9274923..."
                                    className="mt-1 w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                                  />
                                </label>
                                <Button
                                  onClick={() => executePayout.mutate({
                                    cycleId: c.id,
                                    pixTransactionId: txId || undefined,
                                    needsApprove,
                                  })}
                                  disabled={executePayout.isPending || (!c.pixKey && !txId)}
                                  className="bg-teal-500 hover:bg-teal-600 text-white shrink-0 h-9"
                                >
                                  {executePayout.isPending
                                    ? 'Enviando...'
                                    : needsApprove
                                      ? 'Aprovar NF + Confirmar repasse'
                                      : 'Confirmar repasse'}
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </>
  );
}


/**
 * Card de saldo da conta Banco Inter — exibido no topo da página.
 * Lê via GET /admin/payouts/balance. Valores chegam em centavos.
 * PIX OUT no Inter é gratuito pra conta PJ — sem custo por transferência.
 */
function InterBalanceCard({
  loading,
  error,
  balance,
  totalReady,
  onRefresh,
}: {
  loading: boolean;
  error?: string;
  balance?: { total: number; available: number; blocked: number };
  totalReady: number;
  onRefresh: () => void;
}) {
  const available = (balance?.available ?? 0) / 100;
  const blocked = (balance?.blocked ?? 0) / 100;
  const lowBalance = !error && available < totalReady;

  return (
    <div
      className={cn(
        'mb-4 rounded-2xl border p-4 flex items-center justify-between gap-4 flex-wrap',
        error
          ? 'border-red-200 bg-red-50'
          : lowBalance
            ? 'border-amber-200 bg-amber-50'
            : 'border-teal-200 bg-teal-50',
      )}
    >
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Saldo Banco Inter (PIX OUT)</p>
        {error ? (
          <p className="mt-1 text-sm text-red-700">⚠ {error}</p>
        ) : (
          <>
            <p className="mt-1 text-2xl font-bold text-slate-800">
              {available.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              <span className="ml-2 text-xs font-medium text-slate-500">disponível</span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {blocked > 0 && (
                <>
                  {blocked.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} bloqueado ·{' '}
                </>
              )}
              PIX OUT gratuito (conta PJ Inter)
            </p>
            {lowBalance && (
              <p className="text-xs text-amber-800 mt-1 font-medium">
                ⚠ Saldo insuficiente para repassar tudo da fila ({totalReady.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}). Recarregue a conta Inter.
              </p>
            )}
          </>
        )}
      </div>
      <Button variant="outline" onClick={onRefresh} disabled={loading} className="shrink-0">
        {loading ? 'Atualizando...' : '↻ Atualizar saldo'}
      </Button>
    </div>
  );
}

/**
 * Célula da coluna "NF · Validação" — combina link pro PDF + badge de status,
 * derivando o tom (verde/âmbar/vermelho/cinza) e a primeira mensagem de erro/aviso
 * pra dar contexto ao admin sem precisar abrir a tela de revisão.
 */
function ValidationCell({ payout: c }: { payout: Payout }) {
  if (c.status === 'NF_PENDING') {
    return <span className="text-[11px] text-amber-600 font-medium whitespace-nowrap">⏳ Aguardando envio</span>;
  }

  const meta: Record<ValidationStatus, { label: string; cls: string }> = {
    SUCCESS: { label: '✓ Sucesso', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
    WARNING: { label: '⚠ Atenção', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    ERROR:   { label: '✗ Erro', cls: 'bg-red-50 text-red-700 border-red-200' },
    NO_NF:   { label: 'Sem NF', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
    LEGACY:  { label: 'Sem validação', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  };
  const m = meta[c.validationStatus] ?? meta.LEGACY;

  // Pega a primeira mensagem ⚠/✗ pra mostrar como tooltip / linha auxiliar
  const issueMsg = c.invoiceNF?.validationMessages?.find((s) => s.startsWith('⚠') || s.startsWith('✗'));

  return (
    <div className="space-y-1 min-w-0">
      <span
        className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap inline-block', m.cls)}
        title={issueMsg ?? undefined}
      >
        {m.label}
      </span>
      {c.invoiceNF?.nfsePdfUrl && (
        <div>
          <a
            href={c.invoiceNF.nfsePdfUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-teal-600 hover:underline whitespace-nowrap"
          >
            Ver PDF →
          </a>
        </div>
      )}
    </div>
  );
}
